import { useState, useEffect, useMemo } from 'react';
import { authFetch } from '../../utils';
import { API_URL } from '../../config';
import { batteryPercentage } from '../../helpers/battery';

const DAYS = 30;

function formatDate(ts) {
  return new Date(ts).toLocaleString('es-ES');
}

function formatBattery(mv) {
  const pct = batteryPercentage(mv);
  return pct != null ? `${pct} %` : '—';
}

const thStyle = { padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '13px', color: '#495057' };
const tdStyle = { padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: '13px' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: '6px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' };

export default function AlarmsLog() {
  const [observations, setObservations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDetectors, setSelectedDetectors] = useState([]);
  const [detectorDropdownOpen, setDetectorDropdownOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      authFetch(`${API_URL}smoke-detectors/alarm-observations?days=${DAYS}`).then(r => r.json()),
      authFetch(`${API_URL}smoke-detectors/alarm-notifications?days=${DAYS}`).then(r => r.json()),
    ]).then(([obs, notif]) => {
      setObservations(Array.isArray(obs) ? obs : []);
      setNotifications(Array.isArray(notif) ? notif : []);
    }).finally(() => setLoading(false));
  }, []);

  const detectorOptions = useMemo(() => {
    const map = new Map();
    observations.forEach(o => {
      const id = o.detector?.detectorId;
      if (id && !map.has(id)) map.set(id, o.detector?.name);
    });
    return Array.from(map.entries()).map(([detectorId, name]) => ({ detectorId, name }));
  }, [observations]);

  const filteredObservations = selectedDetectors.length
    ? observations.filter(o => selectedDetectors.includes(o.detector?.detectorId))
    : observations;

  const toggleDetector = (detectorId) => {
    setSelectedDetectors(prev =>
      prev.includes(detectorId) ? prev.filter(d => d !== detectorId) : [...prev, detectorId]
    );
  };

  if (loading) return <p style={{ color: '#6c757d' }}>Cargando alarmas...</p>;

  return (
    <div>
      <h2 style={{ marginBottom: '1.5rem' }}>Alarmas (últimos {DAYS} días)</h2>

      <h3 style={{ marginBottom: '0.5rem' }}>🔴 Observaciones con alarma ({filteredObservations.length})</h3>
      <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '0.75rem' }}>
        Observaciones en las que el detector reportó <code>alarm: true</code>.
      </p>

      {detectorOptions.length > 0 && (
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1rem' }}>
          <button
            type="button"
            onClick={() => setDetectorDropdownOpen(o => !o)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: '13px'
            }}
          >
            Filtrar{selectedDetectors.length > 0 ? ` (${selectedDetectors.length})` : ''} ▾
          </button>

          {detectorDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: '#fff',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              padding: '0.5rem',
              zIndex: 10,
              minWidth: '240px',
              maxHeight: '260px',
              overflowY: 'auto'
            }}>
              {detectorOptions.map(d => (
                <label
                  key={d.detectorId}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '13px', padding: '0.25rem 0', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  <input
                    type="checkbox"
                    checked={selectedDetectors.includes(d.detectorId)}
                    onChange={() => toggleDetector(d.detectorId)}
                  />
                  {d.detectorId}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {filteredObservations.length === 0 ? (
        <p style={{ color: '#6c757d', marginBottom: '2rem' }}>Sin observaciones de alarma en los últimos {DAYS} días.</p>
      ) : (
        <div style={{ overflowX: 'auto', marginBottom: '2.5rem' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Detector</th>
                <th style={thStyle}>Temperatura</th>
                <th style={thStyle}>Humedad</th>
                <th style={thStyle}>Batería</th>
                <th style={thStyle}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {filteredObservations.map(o => (
                <tr key={o._id}>
                  <td style={tdStyle}>{o.detector?.detectorId ?? '—'}</td>
                  <td style={tdStyle}>{o.temperature != null ? `${o.temperature} °C` : '—'}</td>
                  <td style={tdStyle}>{o.humidity != null ? `${o.humidity} %` : '—'}</td>
                  <td style={{ ...tdStyle, color: batteryPercentage(o.batteryVoltage) < 20 ? '#dc2626' : 'inherit', fontWeight: batteryPercentage(o.batteryVoltage) < 20 ? 600 : 'normal' }}>{formatBattery(o.batteryVoltage)}</td>
                  <td style={tdStyle}>{formatDate(o.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ marginBottom: '0.5rem' }}>🚨 Notificaciones enviadas ({notifications.length})</h3>
      <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '0.75rem' }}>
        Alertas que superaron el criterio de consistencia y generaron notificaciones reales.
      </p>
      {notifications.length === 0 ? (
        <p style={{ color: '#6c757d' }}>Sin notificaciones de alarma en los últimos {DAYS} días.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Detector</th>
                <th style={thStyle}>Mensaje</th>
                <th style={thStyle}>SMS</th>
                <th style={thStyle}>Llamada</th>
                <th style={thStyle}>Telegram</th>
                <th style={thStyle}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {notifications.map(n => (
                <tr key={n._id}>
                  <td style={tdStyle}>{n.detector?.detectorId ?? '—'}</td>
                  <td style={{ ...tdStyle, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={n.message}>{n.message}</td>
                  <td style={tdStyle}>{n.sms ? '✓' : '—'}</td>
                  <td style={tdStyle}>{n.phoneCall ? '✓' : '—'}</td>
                  <td style={tdStyle}>{n.telegram ? '✓' : '—'}</td>
                  <td style={tdStyle}>{formatDate(n.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
